import ComponentBase, { ComponentBaseProps } from "../../../../components/ComponentBase";
import RupIcons from "../../../../RupIcons";

export interface DropdownBaseProps extends ComponentBaseProps {
	readonly id: string;
	readonly buttons: { text: string, selected: boolean, action: () => void }[];
}

export default abstract class DropdownBase<TProps extends DropdownBaseProps> extends ComponentBase<DropdownBaseProps>{

	protected itemsButton: JQuery[];
	protected menu: JQuery;
	protected toggleButton: JQuery;
	private isShown: boolean;
	private listId = `${this.props.id}-list`;

	private hidingEvents = "mousedown click";

	constructor(props: TProps) {
		super(props);
		this.documentClick = this.documentClick.bind(this);
	}

	public toggle(): void {
		this.isShown ? this.hide() : this.show();
	}

	private show(): void {
		this.getDocuments().on(this.hidingEvents, this.documentClick);
		this.toggleState(true);
	}

	private hide(): void {
		this.getDocuments().off(this.hidingEvents, this.documentClick);
		this.toggleState(false);
	}

	private toggleState(state: boolean) {
		this.isShown = state;
		this.toggleButton.attr("aria-expanded", state.toString());
		this.menu.toggle(state);
	}

	protected renderInternal(): JQuery {
		this.itemsButton = [];
		this.itemsButton.push(...this.getButtons());
		this.menu = this.renderMenu();
		this.menu.hide();

		const buttonToggle = this.props.buttons.filter(button => button.selected == true)[0];
		this.toggleButton = this.renderToggleButton(buttonToggle.text);

		return this.renderContainer();
	}

	private getButtons() {
		return this.props.buttons.map(button => this.renderItemButton(button.text, button.selected, button.action));
	}

	private renderMenu() {
		return $("<div>")
			.addClass("rup-global-search__dropdown-menu rup-elevation globalSearch__dropdown-menu")
			.append(this.itemsButton)
			.attr({
				role: "listbox",
				tabindex: "-1",
				"aria-labelledby": this.listId
			});
	}

	private renderItemButton(text: string, selected = false, action: () => void): JQuery {
		return $("<button>")
			.addClass("rup-global-search__dropdown-item globalSearch__button")
			.toggleClass("rup-global-search__dropdown-item--active", selected)
			.append(selected ? RupIcons.checkMark : "")
			.append(text)
			.attr({
				role: "option",
				"aria-selected": `${selected}`,
				"data-test-id": "search-operator-dropdown-opt"
			})
			.click((e) => { e.preventDefault(); action(); this.isShown = false; });
	}

	private renderHiddenField() {
		return $("<p>")
			.addClass("rup-visually-hidden")
			.text("Choose an element:")
			.attr({
				id: this.props.id
			});
	}

	private renderToggleButton(text: string, expanded = false): JQuery {
		return $("<button>")
			.addClass("rup-global-search__dropdown-toggle globalSearch__button-toggle")
			.append(text)
			.append(RupIcons.chevronDown)
			.attr({
				"aria-haspopup": "listbox",
				"aria-expanded": expanded,
				"aria-labelledby": `${this.props.id} ${this.listId}`,
				id: this.listId,
				"data-test-id": "search-operator-dropdown-btn"
			})
			.click(e => {
				e.stopPropagation();
				this.toggleButtonClick(e);
			});
	}

	protected updateToggleText(text: string): void {
		const newToggleButton = this.renderToggleButton(text);
		$(this.toggleButton).replaceWith(newToggleButton);
		this.toggleButton = $(newToggleButton);
		this.props.buttons.forEach(button => {
			if (button.text === text) {
				button.selected = true;
			} else {
				button.selected = false;
			}
		});
		this.itemsButton = [];
		this.itemsButton.push(...this.getButtons());
		this.menu.empty().append(this.itemsButton);

	}

	private toggleButtonClick(e: JQueryEventObject) {
		e.preventDefault();

		this.menu[0].style.opacity = '0';
		this.toggle();

		this.menu[0].animate([
			{ opacity: 0, transform: 'scale(0.95)' },
			{ opacity: 1, transform: 'scale(1)' }
		], {
			duration: 200,
			iterations: 1
		}).onfinish = () => {
			this.menu[0].style.opacity = '';
		};
	}

	private renderContainer(): JQuery {
		return this.renderContainerInternal()
			.append(this.renderHiddenField())
			.append(this.toggleButton)
			.append(this.menu)
			.attr("aria-describedby", this.props.id);
	}

	private getDocuments(): JQuery {
		return getDocumentIFrames(document);
	}

	private documentClick(event: Event): void {
		if (!$(event.target).closest(this.container).length) {
			this.hide();
		}
	}

	protected hideOnClick(handler?: (...args: unknown[]) => void, ...args: unknown[]): void {
		this.menu.hide();
		handler?.apply(this, args);
	}

	protected abstract renderContainerInternal(): JQuery;
}
